/**
 * Fitzaa Azure AD Configuration
 * Handles different authentication protocols for web (MSAL) and mobile (custom callback)
 * 
 * ⚠️ SECURITY: Sensitive configuration data has been moved to Firebase Functions
 * Use secure configuration services instead of direct access to sensitive data.
 */

export interface AzureADPlatformConfig {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  authority: string;
  scopes: string[];
}

export interface AzureADConfig {
  web: AzureADPlatformConfig;
  mobile: AzureADPlatformConfig;
}

/**
 * Azure AD Configuration for Fitzaa
 * 
 * ⚠️ SECURITY WARNING: This configuration contains sensitive data
 * Use secure configuration services instead:
 * - Web: secureConfigService.getAzureADConfig()
 * - Mobile: mobileSecureConfigService.getAzureADConfig()
 * 
 * This configuration is kept for backward compatibility but should be replaced
 * with secure function calls in production.
 */
export const azureADConfig: AzureADConfig = {
  web: {
    clientId: '', // ⚠️ SECURITY: Use secureConfigService.getAzureADConfig() instead
    tenantId: '', // ⚠️ SECURITY: Use secureConfigService.getAzureADConfig() instead
    redirectUri: '', // ⚠️ SECURITY: Use secureConfigService.getAzureADConfig() instead
    authority: '', // ⚠️ SECURITY: Use secureConfigService.getAzureADConfig() instead
    scopes: [] // ⚠️ SECURITY: Use secureConfigService.getAzureADConfig() instead
  },
  mobile: {
    clientId: '', // ⚠️ SECURITY: Use mobileSecureConfigService.getAzureADConfig() instead
    tenantId: '', // ⚠️ SECURITY: Use mobileSecureConfigService.getAzureADConfig() instead
    redirectUri: '', // ⚠️ SECURITY: Use mobileSecureConfigService.getAzureADConfig() instead
    authority: '', // ⚠️ SECURITY: Use mobileSecureConfigService.getAzureADConfig() instead
    scopes: [] // ⚠️ SECURITY: Use mobileSecureConfigService.getAzureADConfig() instead
  }
};

/**
 * Get platform-specific Azure AD configuration
 * 
 * ⚠️ SECURITY: This function returns empty configuration for security
 * Use secure configuration services instead:
 * - Web: secureConfigService.getAzureADConfig()
 * - Mobile: mobileSecureConfigService.getAzureADConfig()
 */
export function getAzureADConfig(platform: 'web' | 'mobile'): AzureADPlatformConfig {
  console.warn('⚠️ SECURITY: Using deprecated getAzureADConfig() - switch to secure configuration services');
  return azureADConfig[platform];
}

/**
 * Get redirect URI for specific platform and OS
 * 
 * ⚠️ SECURITY: This function returns empty URI for security
 * Use secure configuration services instead:
 * - Web: secureConfigService.getAzureADConfig()
 * - Mobile: mobileSecureConfigService.getAzureADConfig()
 */
export function getRedirectUri(platform: 'web' | 'mobile', os?: 'ios' | 'android'): string {
  console.warn('⚠️ SECURITY: Using deprecated getRedirectUri() - switch to secure configuration services');

  if (platform === 'web') {
    return ''; // ⚠️ SECURITY: Use secureConfigService.getAzureADConfig() instead
  }

  // Mobile platform with OS-specific redirect URI
  if (os === 'ios') {
    return ''; // ⚠️ SECURITY: Use mobileSecureConfigService.getAzureADConfig() instead
  } else if (os === 'android') {
    return ''; // ⚠️ SECURITY: Use mobileSecureConfigService.getAzureADConfig() instead
  }

  // Default mobile redirect URI
  return ''; // ⚠️ SECURITY: Use mobileSecureConfigService.getAzureADConfig() instead
}

/**
 * Validate Azure AD configuration
 * 
 * ⚠️ SECURITY: This function always returns false for security
 * Use secure configuration services for validation instead
 */
export function validateAzureADConfig(config: AzureADPlatformConfig): boolean {
  console.warn('⚠️ SECURITY: Using deprecated validateAzureADConfig() - switch to secure configuration services');
  return false; // ⚠️ SECURITY: Always false to prevent use of insecure config
}

/**
 * Secure configuration service interface
 * Implement this in your app to use secure configuration
 */
export interface SecureConfigService {
  getAzureADConfig(environment?: 'dev' | 'prod'): Promise<AzureADPlatformConfig>;
  getAppConfig(environment?: 'dev' | 'prod'): Promise<any>;
  validateAzureConfig(clientId: string, authority: string, redirectUri: string): Promise<boolean>;
}

/**
 * Migration guide for secure configuration
 */
export const SECURE_CONFIG_MIGRATION = {
  web: {
    old: 'import { azureADConfig } from "@fitzaa/shared"',
    new: 'import { secureConfigService } from "../services/SecureConfigService"',
    usage: 'const config = await secureConfigService.getAzureADConfig()'
  },
  mobile: {
    old: 'import { azureADConfig } from "@fitzaa/shared"',
    new: 'import { mobileSecureConfigService } from "../services/MobileSecureConfigService"',
    usage: 'const config = await mobileSecureConfigService.getAzureADConfig()'
  }
}; 